import type { ReactNode } from 'react';
import {
  CalendarDaysIcon,
  MapPinIcon,
  MessageSquareTextIcon,
  TagIcon,
  TicketIcon,
  UsersIcon,
  type LucideIcon,
} from 'lucide-react';

import type { AdminEvent } from '@mds/shared/types/event';

import Chip from '@/components/ui/chip';
import {
  eventDateLabel,
  eventFeedbackTone,
  eventPriceLabel,
  eventStatusTone,
  eventWhen,
} from '@/domain/events';
import { clockLabel } from '@mds/shared/lib/dates';

/** A titled block on the event detail, headed the way the public page heads it. */
function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-3">
      <h3 className="m-0 border-b border-hairline pb-2 font-mono text-md font-semibold tracking-wider text-brand-text uppercase">
        {title}
      </h3>
      {children}
    </section>
  );
}

/** One cell of the facts grid: an icon, a small label, the value and an optional line under it. */
function Fact({
  icon: Icon,
  label,
  children,
  sub,
}: {
  icon: LucideIcon;
  label: string;
  children: ReactNode;
  sub?: ReactNode;
}) {
  return (
    <div className="flex gap-3 bg-surface p-4">
      <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-brand-wash text-brand-text">
        <Icon className="size-4" aria-hidden />
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="font-mono text-2xs tracking-wider text-ink-muted uppercase">{label}</span>
        <div className="text-base font-semibold text-ink">{children}</div>
        {sub && <div className="text-sm text-ink-muted">{sub}</div>}
      </div>
    </div>
  );
}

/** One event as the programme reads it: the gallery, the figures and the copy. */
export default function EventDetails({ event }: { event: AdminEvent }) {
  const tone = eventStatusTone(event.status);
  const feedback = eventFeedbackTone(event.feedback);
  const rest = event.media.filter((item) => !item.isCover);
  const filled = Math.min(100, Math.round((event.registeredSeats / event.capacity) * 100));
  const left = Math.max(0, event.capacity - event.registeredSeats);
  const status = (
    <Chip size="sm" className={'border ' + tone.cls}>
      {tone.label}
    </Chip>
  );

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        {event.cover ? (
          <div className="relative">
            <img
              src={event.cover.contentUrl}
              alt=""
              className="h-52 w-full rounded-2xl object-cover"
            />
            {/* The tone's wash is translucent, so it gets a solid backing over the photo. */}
            <div className="absolute top-3 left-3 rounded-full bg-surface shadow-lift">
              {status}
            </div>
          </div>
        ) : (
          <div>{status}</div>
        )}
        {rest.length > 0 && (
          <ul className="m-0 flex list-none gap-2 overflow-x-auto p-0">
            {rest.map((item) => (
              <li key={item.fileId} className="shrink-0">
                <img
                  src={item.contentUrl}
                  alt=""
                  className="h-14 w-20 rounded-lg border border-hairline object-cover"
                />
              </li>
            ))}
          </ul>
        )}
      </div>

      <p className="m-0 text-lg leading-snug font-semibold text-ink">{event.shortDescription}</p>

      {/* The hairline shows through a one-pixel gap, so the grid draws its own
          dividers whichever way the cells wrap. */}
      <div className="grid gap-px overflow-hidden rounded-2xl border border-hairline bg-hairline sm:grid-cols-2">
        <Fact icon={CalendarDaysIcon} label="When" sub={eventWhen(event)}>
          {eventDateLabel(event.eventDate)}
        </Fact>
        <Fact icon={MapPinIcon} label="Where">
          {event.space}
        </Fact>
        <Fact icon={TagIcon} label="Category">
          {event.category}
        </Fact>
        <Fact icon={TicketIcon} label="Price" sub={event.priceNote || undefined}>
          {eventPriceLabel(event.price.amountMinor)}
        </Fact>
        <Fact
          icon={UsersIcon}
          label="Seats"
          sub={
            <span className="flex items-center gap-2">
              <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-ink/10">
                <span
                  className="block h-full rounded-full bg-brand"
                  style={{ width: filled + '%' }}
                />
              </span>
              <span className="shrink-0">{left === 0 ? 'Full' : `${left} left`}</span>
            </span>
          }
        >
          {event.registeredSeats} of {event.capacity} registered
        </Fact>
        <Fact icon={MessageSquareTextIcon} label="Feedback">
          {feedback ? (
            <Chip size="sm" className={'border ' + feedback.cls}>
              {feedback.label}
            </Chip>
          ) : (
            <span className="font-normal text-ink-muted">Not set up</span>
          )}
        </Fact>
      </div>

      {event.longDescription && (
        <Section title="About">
          <p className="m-0 text-base leading-relaxed whitespace-pre-line text-ink-secondary">
            {event.longDescription}
          </p>
        </Section>
      )}

      {event.agenda.length > 0 && (
        <Section title="Run of the evening">
          <ul className="m-0 flex list-none flex-col gap-2 p-0">
            {event.agenda.map((row, i) => (
              <li key={i} className="flex gap-3 border-b border-hairline/70 pb-2 text-base">
                <span className="w-20 shrink-0 font-mono text-md text-brand-text">
                  {row.startMinute === null ? '' : clockLabel(row.startMinute)}
                </span>
                <span>{row.description}</span>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {event.goodToKnow.length > 0 && (
        <Section title="Good to know">
          <dl className="m-0 grid gap-4 sm:grid-cols-2">
            {event.goodToKnow.map((row, i) => (
              <div key={i} className="border-t border-hairline pt-2">
                <dt className="font-mono text-sm tracking-wide text-brand-text uppercase">
                  {row.label}
                </dt>
                <dd className="m-0 mt-1 text-base">{row.value}</dd>
              </div>
            ))}
          </dl>
        </Section>
      )}
    </div>
  );
}
